import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";

const inviteSchema = z.object({
  email: z.string().email("Invalid email address"),
  name: z.string().optional(),
});
type InviteFormData = z.infer<typeof inviteSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteClientDialog({ open, onOpenChange }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: "", name: "" },
  });

  // D-170.6: converted from a raw apiRequest to the house pattern. Class A, and the harm is
  // server-side visible: `/invite-client` does `db.insert(advisorInvites)` with NO conflict
  // guard (advisor-routes.ts:394), so a double-submit inserts a DUPLICATE invite row and sends
  // a second email. Until the server dedupes, this lock is the only thing preventing that.
  const inviteFlight = useSingleFlight();
  const inviteMutation = useMutation({
    mutationFn: async (data: InviteFormData) => {
      const res = await apiRequest("POST", "/api/advisor/invite-client", data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to send invitation");
      }
      return await res.json();
    },
  });

  const onSubmit = async (data: InviteFormData) => {
    setIsLoading(true);
    try {
      const result = await inviteFlight.run(() => inviteMutation.mutateAsync(data));
      if (!result) return; // duplicate fire — the original owns the outcome
      queryClient.invalidateQueries({ queryKey: ["/api/advisor/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/advisor/invites"] });
      queryClient.invalidateQueries({ queryKey: ["/api/advisor/stats"] });
      toast({
        title: result.status === "linked" ? "Client linked" : "Invitation sent",
        description: result.status === "linked"
          ? `${data.email} has been linked to your account.`
          : `Invitation sent to ${data.email}.`,
      });
      form.reset();
      onOpenChange(false);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite a Client</DialogTitle>
          <DialogDescription>
            Send an invitation email with your discount code pre-applied. If they're already
            registered, they'll be linked to your account immediately.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField control={form.control} name="email" render={({ field }) => (
              <FormItem>
                <FormLabel>Client Email</FormLabel>
                <FormControl>
                  <Input type="email" placeholder="client@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="name" render={({ field }) => (
              <FormItem>
                <FormLabel>Client Name <span className="text-muted-foreground">(optional)</span></FormLabel>
                <FormControl>
                  <Input placeholder="Jane Doe" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading || inviteFlight.inFlight} data-testid="button-send-invite">
                {isLoading ? "Sending..." : "Send Invitation"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
